import React, { useEffect, useState } from "react";
import { collection, getDocs, addDoc, doc, getDoc } from "firebase/firestore";
import jsPDF from "jspdf";
import "jspdf/dist/polyfills.es.js";
import autoTable from "jspdf-autotable";

import "./App.css";
import db from "./firebase/firebase_config";
import { DejaVuFont } from "./DejaVuFont";

// Image fetch
const getImageUrl = (filename, type = "") => {
  const cleaned = filename.replace(/\.png$/i, "").replace(/\s/g, "").toLowerCase();

  if (type === "cup" || type === "cover") {
    return `https://res.cloudinary.com/dtgckob3k/image/upload/f_auto,q_auto,c_pad,w_200,h_260,b_white/${cleaned}`;
  }

  // default (color, packaging, carton, consumables etc.)
  return `https://res.cloudinary.com/dtgckob3k/image/upload/f_auto,q_auto,c_fit/${cleaned}`;
};

const options = {
  material: ["TRITAN", "POLYCARBON"],
  cup: ["Junior_500ml", "Dumble_650ml", "Thinny_700ml", "Middon_350ml", "Maxxon_600ml"],
  cover: ["Targo", "Lento", "Diego", "Camry"],
  color: [
    "Şeffaf",
    "Antrasit",
    "Kırmızı",
    "Koyu Mavi",
    "Açık Mavi",
    "Mor",
    "Pembe",
    "Sarı",
    "Turkuaz",
    "Turuncu",
    "Yeşil",
    "Özel_Renk",
  ],
  packaging: ["Ambalaj yok", "Tekli Poşet", "Tekli Craft Kutu"],
  carton: ["6", "12", "24", "48"],
  print: ["Baskı yok", "1 Renk Baskı", "2 Renk Baskı"],
  sarf: [
    "Boyunluk",
    "Sleeve Kart",
    "Sallantı Kartı",
    "Fiyat Barkodu",
    "Koli Etiketi",
    "Akordiyon kullanma kılavuzu",
  ],
  payment: ["Peşin", "30 Gün Vade", "60 Gün Vade"],
  transport: ["Dudullu Fabrika Teslim", "İstanbul İçi Teslim"],
};

// === UI text translations (display only; internal values remain Turkish for Firebase matching) ===
const displayMaps = {
  color: {
    Şeffaf: "Transparent",
    Antrasit: "Anthracite",
    Kırmızı: "Red",
    "Koyu Mavi": "Dark Blue",
    "Açık Mavi": "Light Blue",
    Mor: "Purple",
    Pembe: "Pink",
    Sarı: "Yellow",
    Turkuaz: "Turquoise",
    Turuncu: "Orange",
    Yeşil: "Green",
    Özel_Renk: "Custom Color",
  },
  packaging: {
    "Ambalaj yok": "No Packaging",
    "Tekli Poşet": "Single Poly Bag",
    "Tekli Craft Kutu": "Single Craft Box",
  },
  print: {
    "Baskı yok": "No Print",
    "1 Renk Baskı": "1-Color Print",
    "2 Renk Baskı": "2-Color Print",
  },
  payment: {
    Peşin: "Cash",
    "30 Gün Vade": "30 Days",
    "60 Gün Vade": "60 Days",
    "90 Gün Vade": "90 Days",
  },
  transport: {
    "Dudullu Fabrika Teslim": "Ex-Works (Dudullu Factory)",
    "İstanbul İçi Teslim": "Delivery within Istanbul",
  },
  sarf: {
    Boyunluk: "Lanyard",
    "Sleeve Kart": "Sleeve Card",
    "Sallantı Kartı": "Hanging Card",
    "Fiyat Barkodu": "Price Barcode",
    "Koli Etiketi": "Carton Label",
    "Akordiyon kullanma kılavuzu": "Accordion User Guide",
  },
  carton: {
    "6": "Master Carton (6) with Separators",
    "12": "Master Carton (12) with Separators",
    "24": "Master Carton (24) with Separators",
    "48": "Master Carton (48)",
  },
};

const tOption = (groupKey, value) => displayMaps?.[groupKey]?.[value] ?? value;

function App() {
  const [form, setForm] = useState({ amount1: "", amount2: "", amount3: "" });
  const [summary, setSummary] = useState(null);
  const [cupWeights, setCupWeights] = useState([]); // products
  const [covers, setCovers] = useState([]); // covers
  const [cartons, setCartons] = useState([]); // carton
  const [packagings, setPackagings] = useState([]); // packaging
  const [colors, setColors] = useState([]); // colors
  const [prints, setPrints] = useState([]); // printing
  const [sarfMaterials, setSarfMaterials] = useState([]); // consumables
  const [payments, setPayments] = useState([]); // payment terms
  const [transports, setTransports] = useState([]); // shipping
  const [usdRate, setUsdRate] = useState(null);
  const [eurRate, setEurRate] = useState(null);

  const [hammaddeFiyat, setHammaddeFiyat] = useState(0);
  const [hammaddeElektrikKatsayisi, setHammaddeElektrikKatsayisi] = useState(0);
  const [bardakAgirlik, setBardakAgirlik] = useState(0);

  const [secilenKapakAgirlik, setSecilenKapakAgirlik] = useState(0);
  const [secilenKapakFiyat, setSecilenKapakFiyat] = useState(0);
  const [secilenKapakMarka, setSecilenKapakMarka] = useState("");

  const [secilenRenkAgirlik, setSecilenRenkAgirlik] = useState(0);
  const [secilenRenkFiyat, setSecilenRenkFiyat] = useState(0);
  const [secilenRenkKullanimOrani, setSecilenRenkKullanimOrani] = useState(0);

  const [ambalajFiyat, setAmbalajFiyat] = useState(0);

  const [koliKacli, setKoliKacli] = useState(0);
  const [koliFiyat, setKoliFiyat] = useState(0);
  const [koliBirimKoliFiyatiTl, setKoliBirimKoliFiyatiTl] = useState(0); // 1 carton total TL

  const [serigrafiFiyat, setSerigrafiFiyat] = useState(0);
  const [sarfToplamFiyat, setSarfToplamFiyat] = useState(0);
  const [minUretimSayisi, setMinUretimSayisi] = useState(1);

  const [bardakFiyat, setBardakFiyat] = useState(0);
  const [bardakBaskiFiyat, setBardakBaskiFiyat] = useState(0);
  const [renkegoreFiyat, setRenkFiyat] = useState(0);

  const [tnsKarMarji, setTnsKarMarji] = useState(0);
  const [farisKarMarji, setFarisKarMarji] = useState(0);
  const [tnsKarTl, setTnsKarTl] = useState(0);
  const [farisKarTl, setFarisKarTl] = useState(0);
  const [tnsTl, setTnsTl] = useState(0);
  const [farisTl, setFarisTl] = useState(0);

  const [birimToplamMaliyet, setBirimToplamMaliyet] = useState(0);

  const [isBlocking, setIsBlocking] = useState(false);

  const [koliHacimM3, setKoliHacimM3] = useState(0); // m³
  const [koliAgirlikKg, setKoliAgirlikKg] = useState(0); // kg
  const [koliOlcusu, setKoliOlcusu] = useState(""); // "64 x 26 x 26 cm"

  const sarfMapping = {
    Boyunluk: "Boyunluk",
    "Sleeve Kart": "Sleevekart",
    "Sallantı Kartı": "SallantıKartı",
    "Fiyat Barkodu": "ÜrünFiyatBarkodu",
    "Koli Etiketi": "KoliBarkodu",
    "Akordiyon kullanma kılavuzu": "Kılavuz",
  };

  const showBlockingOverlay = () => {
    setIsBlocking(true);
    setTimeout(() => setIsBlocking(false), 5000);
  };

  const paymentMapping = {
    Peşin: "pesin",
    "30 Gün Vade": "otuz",
    "60 Gün Vade": "altmis",
    "90 Gün Vade": "doksan",
  };
  const [secilenOdemeVadesiGun, setSecilenOdemeVadesiGun] = useState(0);

  const nakliyeMapping = {
    "Dudullu Fabrika Teslim": "dudullu",
    "İstanbul İçi Teslim": "istanbulici",
  };
  const [girilenIstanbulIci, setGirilenIstanbulIci] = useState(0);

  const [birimElektrikMaliyeti, setBirimElektrikMaliyeti] = useState(0);
  const [birimIscilikFiyati, setBirimIscilikFiyati] = useState(0);

  // qty formatting (Turkish thousand separators)
  const formatNumber = (value) => {
    const num = value.replace(/\D/g, "");
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const parseAmount = (v) => {
    const n = parseInt((v || "").toString().replace(/\./g, ""), 10);
    return Number.isFinite(n) ? n : 0;
  };

  const fetchMinUretimSayisi = async () => {
    const ref = doc(db, "MinUretimSayisi", "TkMinUretimSayisi");
    const snap = await getDoc(ref);

    if (snap.exists()) {
      const data = snap.data();
      console.log("📦 Min production from Firebase:", data.uretimsayisi);
      return data.uretimsayisi || 1;
    } else {
      console.warn("🚫 Document not found");
      return 1;
    }
  };

  const hesaplaBardakFiyati = (hammadde, agirlik) => {
    if (hammadde > 0 && agirlik > 0) {
      const fiyat = (hammadde * agirlik) / 1000;
      setBardakFiyat(fiyat);
      console.log("Cup Material Cost:", fiyat.toFixed(2));
    }
  };

  const hesaplaBardakBaskiFiyati = (iscilik, elektrik) => {
    if (iscilik > 0 && elektrik > 0) {
      const fiyat = iscilik + elektrik;
      setBardakBaskiFiyat(fiyat);
      console.log("Cup Production Cost:", fiyat.toFixed(2));
    }
  };

  const hesaplaRenkFiyati = (renkKgFiyat, euroKur, kullanimOrani, bardakAgirlikLocal) => {
    if (renkKgFiyat > 0 && euroKur > 0 && kullanimOrani > 0 && bardakAgirlikLocal > 0) {
      const fiyat = (renkKgFiyat * euroKur * kullanimOrani * bardakAgirlikLocal) / 100000;
      console.log("color cost", fiyat);
      setRenkFiyat(fiyat);
    }
  };

  const fetchTnsKarMarji = async () => {
    const ref = doc(db, "TnsMarj", "GirilenTnsMarj");
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      console.log("TNS Margin:", data.marjoran);
      return data.marjoran || 0;
    } else {
      console.warn("TNS margin not found");
      return 0;
    }
  };

  const fetchFarisKarMarji = async () => {
    const ref = doc(db, "FarisMarj", "GirilenFarisMarj");
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      console.log("Faris Margin:", data.marjoran);
      return data.marjoran || 0;
    } else {
      console.warn("Faris margin not found");
      return 0;
    }
  };

  const calculateKarlar = ({
    secilenKapakMarka: marka,
    secilenKapakFiyat: kapakTl,
    bardakFiyat: cupMatTl,
    bardakBaskiFiyat: cupProdTl,
    renkegoreFiyat: colorTl,
    tnsKarMarji: tnsMargin,
    farisKarMarji: farisMargin,
  }) => {
    let tnsKar = 0;
    let farisKar = 0;

    if (marka === "tns" && kapakTl > 0 && tnsMargin > 0) {
      tnsKar = kapakTl * tnsMargin;
    }

    if (
      marka?.toLowerCase().includes("faris") &&
      cupMatTl > 0 &&
      cupProdTl > 0 &&
      colorTl >= 0 &&
      kapakTl > 0 &&
      farisMargin > 0
    ) {
      const toplamTl = cupMatTl + cupProdTl + colorTl + kapakTl;
      farisKar = toplamTl * farisMargin;
    }

    if (marka?.toLowerCase().includes("tnsfaris")) {
      farisKar = (cupMatTl + cupProdTl) * farisMargin;
      tnsKar = kapakTl * tnsMargin;
    }

    setTnsKarTl(tnsKar);
    setFarisKarTl(farisKar);

    return { tnsKar, farisKar };
  };

  const hesaplaBirimToplamMaliyet = ({
    bardakFiyat: cupMatTl,
    bardakBaskiFiyat: cupProdTl,
    renkegoreFiyat: colorTl,
    secilenKapakFiyat: coverTl,
    ambalajFiyat: packagingTl,
    koliFiyat: cartonUnitTl,
    serigrafiFiyat: printTl,
    sarfToplamFiyat: consumablesTl,
    tnsKar,
    farisKar,
    secilenKapakMarka: marka,
  }) => {
    let toplam =
      cupMatTl +
      cupProdTl +
      colorTl +
      coverTl +
      packagingTl +
      cartonUnitTl +
      printTl +
      consumablesTl +
      tnsKar +
      farisKar;

    // if cover brand is tns or tnsfaris -> remove tns margin
    if (marka === "tns" || marka === "tnsfaris") {
      toplam -= tnsKar;
    } else if (marka?.toLowerCase().includes("faris")) {
      toplam = toplam; // keep faris margin
    }

    setBirimToplamMaliyet(toplam);
    return toplam;
  };

  const computePriceForAdet = ({
    adet,
    birimMaliyet,
    koliKacli,
    koliBirimKoliFiyatiTl,
    koliFiyat,
    girilenIstanbulIci,
    secilenOdemeVadesiGun,
    params,
    getVadeFarkOrani,
  }) => {
    if (!adet || adet < 500) return null;

    const karMarjOrani = getVadeFarkOrani(adet) || 0;

    const girilenOran = params.GirilenVadeFarkOrani || 0;
    const hesaplananVadeFarki = (secilenOdemeVadesiGun * girilenOran) / 360;
    const vadeTl = hesaplananVadeFarki * birimMaliyet;

    const gerekenKoli = Math.ceil(adet / Math.max(1, koliKacli || 1));
    const exactKoliToplamTl = gerekenKoli * (koliBirimKoliFiyatiTl || 0);
    const exactKoliBirimTl = adet > 0 ? exactKoliToplamTl / adet : 0;

    const karMarjiTl = birimMaliyet * karMarjOrani;
    const nakliyeTl = (vadeTl + birimMaliyet) * (girilenIstanbulIci || 0);

    const kdvOrani = 0.2;
    const birimKDVHaric =
      birimMaliyet + vadeTl + nakliyeTl + karMarjiTl - (koliFiyat || 0) + exactKoliBirimTl;
    const birimKDVDahil = birimKDVHaric * (1 + kdvOrani);

    return {
      adet,
      vadeTl,
      karMarjiTl,
      nakliyeTl,
      birimKDVHaric,
      birimKDVDahil,
      gerekenKoli,
    };
  };

  const [quantityDiscounts, setQuantityDiscounts] = useState([]); // AdeteGoreVadeFarkOranlari
  const getVadeFarkOrani = (adet) => {
    if (!quantityDiscounts.length || !adet) return 0;
    const uygunAralik = quantityDiscounts.find((item) => adet >= item.min && adet <= item.max);
    return uygunAralik?.oran || 0;
  };

  const [params, setParams] = useState({});

  useEffect(() => {
    showBlockingOverlay();

    const fetchAllData = async () => {
      const fetchCollection = async (colName) => {
        const snap = await getDocs(collection(db, colName));
        return snap.docs.map((docu) => ({ id: docu.id, ...docu.data() }));
      };

      const tnsMarj = await fetchTnsKarMarji();
      const farisMarj = await fetchFarisKarMarji();
      setTnsKarMarji(tnsMarj);
      setFarisKarMarji(farisMarj);

      const fetchExchangeRates = async () => {
        const response = await fetch("https://open.er-api.com/v6/latest/TRY");
        if (!response.ok) throw new Error("Rates HTTP " + response.status);
      
        const data = await response.json();
        const usd = 1 / data?.rates?.USD;
        const eur = 1 / data?.rates?.EUR;
      
        if (!Number.isFinite(usd) || !Number.isFinite(eur)) {
          throw new Error("Invalid rates response");
        }
      
        return { usd, eur };
      };
      
      try {
        const { usd, eur } = await fetchExchangeRates();
        setUsdRate(usd);
        setEurRate(eur);
        console.log("✅ USD:", usd.toFixed(2), "EUR:", eur.toFixed(2));
      } catch (error) {
        console.error("Exchange rate fetch failed:", error);
        setUsdRate(38);
        setEurRate(43);
      }
      fetchExchangeRates();

      const fetchGeneralParams = async () => {
        const snapshot = await getDocs(collection(db, "ParametrelerGenel"));
        const paramsData = {};

        snapshot.docs.forEach((docu) => {
          const key = docu.id;
          const data = docu.data();
          const value = Object.keys(data).length === 1 ? Object.values(data)[0] : data;
          paramsData[key] = value;
        });

        setParams(paramsData);
        console.log("General parameters:", paramsData);
      };

      const [
        cups,
        coversData,
        cartonsData,
        packagingData,
        colorData,
        printData,
        sarfData,
        paymentData,
        transportData,
        discountData,
      ] = await Promise.all([
        fetchCollection("products"),
        fetchCollection("Kapaklar"),
        fetchCollection("Koli"),
        fetchCollection("Ambalaj"),
        fetchCollection("Renk"),
        fetchCollection("Serigrafibaski"),
        fetchCollection("SarfMalzemeleri"),
        fetchCollection("OdemeVadesiGunleri"),
        fetchCollection("Nakliye"),
        fetchCollection("AdeteGoreVadeFarkOranlari"),
      ]);

      setCupWeights(cups);
      setCovers(coversData);
      setCartons(cartonsData);
      setPackagings(packagingData);
      setColors(colorData);
      setPrints(printData);
      setSarfMaterials(sarfData);
      setPayments(paymentData);
      setTransports(transportData);
      setQuantityDiscounts(discountData);

      const minUretim = await fetchMinUretimSayisi();
      setMinUretimSayisi(minUretim);

      await fetchGeneralParams();
    };

    fetchAllData();
  }, []);

  const addNewOneToDB = async (index) => {
    const collectionRef = collection(db, "products");
    const payload = { name: "product-" + index };
    const resp = await addDoc(collectionRef, payload);
    console.log(resp.id);
  };

  const select = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));

    if (key === "material") {
      if (!minUretimSayisi) {
        console.warn("Min production not loaded yet");
        return;
      }

      const minUretimAdet = minUretimSayisi;

      const isTritan = value === "TRITAN";
      const isPoly = value === "POLYCARBON";

      const kgFiyatBaz = isTritan
        ? params.TritanKgFiyat || 0
        : isPoly
        ? params.PolycarbonKgFiyatUSD ?? params.PolycarbonKgFiyat ?? 0
        : 0;

      const kur = isTritan ? eurRate || 0 : isPoly ? usdRate || 0 : 0;
      const tlFiyat = kgFiyatBaz * kur;

      const katsayi = isTritan
        ? params.TritanElektrikKatsayisi || 0
        : isPoly
        ? params.PolycarbonElektrikKatsayisi || 0
        : 0;

      const elektrikKwFiyat = params.Elektrik_kw_fiyat || 0;
      const elektrikMaliyet = katsayi * elektrikKwFiyat;

      const asgariUcret = params.AsgariUcret || 0;
      const ucretKatsayisi = params.UcretKatsayisi || 0;
      const birimIscilik = (asgariUcret * ucretKatsayisi) / (minUretimAdet || 1);

      setHammaddeFiyat(tlFiyat);
      setHammaddeElektrikKatsayisi(katsayi);
      setBirimElektrikMaliyeti(elektrikMaliyet);
      setBirimIscilikFiyati(birimIscilik);

      hesaplaBardakFiyati(tlFiyat, bardakAgirlik);
      hesaplaBardakBaskiFiyati(birimIscilik, elektrikMaliyet);
    }

    if (key === "cup" && cupWeights.length > 0) {
      const selectedCup = cupWeights.find((cup) => cup.id === value);
      const gram = selectedCup?.weight || 0;
      setBardakAgirlik(gram);
      console.log("Selected Cup:", value, "Weight (g):", gram);
      hesaplaBardakFiyati(hammaddeFiyat, gram);
    }

    if (key === "cover" && covers.length > 0) {
      const selectedCover = covers.find((cover) => cover.id === value);
      const agirlik = selectedCover?.agirlik || 0;
      const fiyatUsd = selectedCover?.fiyat || 0;
      const marka = selectedCover?.marka?.toLowerCase() || "";
      const fiyatTl = fiyatUsd * (usdRate || 0);

      setSecilenKapakAgirlik(agirlik);
      setSecilenKapakFiyat(fiyatTl);
      setSecilenKapakMarka(marka);

      if (marka === "tns") setTnsTl(fiyatTl);
      else if (marka === "faris") setFarisTl(fiyatTl);
    }

    if (key === "color" && colors.length > 0) {
      const selectedColor = colors.find((item) => item.renkadi === value);

      const renkAgirlik = selectedColor?.agirlik || 0;
      const renkFiyat = selectedColor?.fiyat || 0;
      const kullanimOrani = selectedColor?.kullanimorani || 0;

      setSecilenRenkAgirlik(renkAgirlik);
      setSecilenRenkFiyat(renkFiyat);
      setSecilenRenkKullanimOrani(kullanimOrani);
      hesaplaRenkFiyati(renkFiyat, eurRate, kullanimOrani, bardakAgirlik);
    }

    if (key === "packaging") {
      if (value === "Ambalaj yok") {
        setAmbalajFiyat(0);
      } else {
        const selectedAmbalaj = packagings.find((item) => item.id === value);
        const fiyatUsd = selectedAmbalaj?.fiyat || 0;
        const fiyatTl = fiyatUsd * (usdRate || 0);
        setAmbalajFiyat(fiyatTl);
      }
    }

    if (key === "carton" && cartons.length > 0) {
      const secilenKoli = cartons.find((item) => item.adeti?.toString() === value);

      const adet = secilenKoli?.adeti || 0;
      const fiyatUsd = secilenKoli?.fiyat || 0;
      const fiyatTl = fiyatUsd * (usdRate || 0);
      const birimFiyat = adet !== 0 ? fiyatTl / adet : 0;

      setKoliKacli(adet);
      setKoliFiyat(birimFiyat);
      setKoliBirimKoliFiyatiTl(fiyatTl);
      setKoliHacimM3(secilenKoli?.kolihacim || 0);
      setKoliAgirlikKg(secilenKoli?.koliagirlik || 0);
      setKoliOlcusu(secilenKoli?.koliolcusu || "");
    }

    const normalize = (str) => str.toLowerCase().replace(/\s|_/g, "");

    if (key === "print" && prints.length > 0) {
      if (value === "Baskı yok") {
        setSerigrafiFiyat(0);
      } else {
        const secilenBaski = prints.find((item) => normalize(item.id) === normalize(value));
        const fiyatUsd = secilenBaski?.fiyat || 0;
        const fiyatTl = fiyatUsd * (usdRate || 0);
        setSerigrafiFiyat(fiyatTl);
      }
    }

    if (key === "payment" && payments.length > 0) {
      const firestoreId = paymentMapping[value];
      const vadeDoc = payments.find((item) => item.id === firestoreId);
      const gun = vadeDoc?.gun || 0;
      setSecilenOdemeVadesiGun(gun);
    }

    if (key === "transport" && transports.length > 0) {
      const firestoreId = nakliyeMapping[value];

      if (firestoreId === "dudullu") {
        setGirilenIstanbulIci(0);
        console.log("Shipping: Ex-Works, rate: 0");
      } else {
        const secilenNakliye = transports.find((item) => item.id.toLowerCase() === firestoreId);
        const oran = secilenNakliye?.oran || 0;
        setGirilenIstanbulIci(oran);
      }
    }
  };

  const toggleCheckbox = (key, value) => {
    const current = form[key] || [];
    const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];

    setForm((prev) => ({ ...prev, [key]: updated }));

    if (key === "sarf" && sarfMaterials.length > 0) {
      const toplam = updated.reduce((acc, item) => {
        const sarfId = sarfMapping[item];
        const sarfDoc = sarfMaterials.find((mat) => mat.id === sarfId);
        const fiyatUsd = sarfDoc?.fiyat || 0;
        return acc + fiyatUsd * (usdRate || 0);
      }, 0);

      setSarfToplamFiyat(toplam);
    }
  };

  // === PDF function (translated) ===
  const createAdvancedPDF = (formLocal, summaryLocal, usdLocal, eurLocal) => {
    const getImageUrlLocal = (filename, type = "") => {
      const sarfMap = {
        Boyunluk: "boyunluk",
        "Sleeve Kart": "sleeve",
        "Sleeve:1": "sleeve",
        "sleeve:1": "sleeve",
        "Sallantı Kartı": "sallanti",
        "Fiyat Barkodu": "barkod",
        "Koli Etiketi": "etiket",
        "Akordiyon kullanma kılavuzu": "kilavuz",
      };

      const base = "https://res.cloudinary.com/dtgckob3k/image/upload";
      const mapped = sarfMap[filename];

      const cleaned = (mapped || filename)
        .replace(/\.(png|jpg|jpeg)$/i, "")
        .replace(/[^a-z0-9]/gi, "")
        .toLowerCase();

      if (type === "cup" || type === "cover") {
        return `${base}/f_auto,q_auto,c_pad,w_200,h_260,b_white/${cleaned}`;
      }
      return `${base}/f_auto,q_auto,c_fit/${cleaned}`;
    };

    const docPdf = new jsPDF("p", "mm", "a4");
    docPdf.addFileToVFS("DejaVu.ttf", DejaVuFont.base64);
    docPdf.addFont("DejaVu.ttf", "DejaVu", "normal");
    docPdf.setFont("DejaVu", "normal");

    const tasks = [];
    const IMG_WIDTH = 25;
    const IMG_HEIGHT = 25;
    const pageWidth = docPdf.internal.pageSize.getWidth();
    const logoWidth = 40;

    const today = new Date();
    const validUntil = new Date();
    validUntil.setDate(today.getDate() + 7);
    const formatDate = (date) => date.toLocaleDateString("tr-TR");

    // Logo
    tasks.push(
      new Promise((resolve) => {
        const logo = new Image();
        logo.src = getImageUrlLocal("Logo");
        logo.onload = () => {
          const x = (pageWidth - logoWidth) / 2;
          docPdf.addImage(logo, "PNG", x, 10, logoWidth, 15);
          resolve();
        };
        logo.onerror = resolve;
      })
    );

    autoTable(docPdf, {
      startY: 30,
      head: [["Offer No", "Date", "Valid Until"]],
      body: [[summaryLocal.SiparişId, formatDate(today), formatDate(validUntil)]],
      theme: "grid",
      styles: { font: "DejaVu", fontSize: 8 },
      headStyles: { fillColor: [0, 92, 135] },
    });

    autoTable(docPdf, {
      startY: docPdf.lastAutoTable.finalY + 4,
      head: [["Currency", "TRY"]],
      body: [
        ["EUR", eurLocal?.toFixed(2) + " TL" || "-"],
        ["USD", usdLocal?.toFixed(2) + " TL" || "-"],
      ],
      theme: "grid",
      styles: { font: "DejaVu", fontSize: 8 },
      headStyles: { fillColor: [0, 92, 135] },
    });

    autoTable(docPdf, {
      startY: docPdf.lastAutoTable.finalY + 4,
      body: [
        ["Material", formLocal.material],
        ["Cup", formLocal.cup],
        ["Cover", formLocal.cover],
        ["Color", formLocal.color],
        ["Packaging", formLocal.packaging],
        ["Carton", formLocal.carton],
        ["Print", formLocal.print],
        ["Consumables", Array.isArray(formLocal.sarf) ? formLocal.sarf.join("\n") : ""],
      ],
      styles: { font: "DejaVu", fontSize: 8 },
      theme: "grid",
    });

    autoTable(docPdf, {
      startY: docPdf.lastAutoTable.finalY + 4,
      head: [["Item", "Amount (TRY)"]],
      body: [
        ["Unit Cost", summaryLocal.birimToplamMaliyet || "-"],
        ["Quantity", formLocal.amount || "-"],
        ["Shipping", summaryLocal.nakliyeTl || "-"],
        ["Unit (Incl. VAT)", summaryLocal.satisFiyatiKDVDahil || "-"],
        ["Total", summaryLocal.Toplam_Tutar || "-"],
        ["Total Weight", summaryLocal.toplamAgirlikKg || "-"],
      ],
      theme: "grid",
      styles: { font: "DejaVu", fontSize: 8 },
      headStyles: { fillColor: [0, 92, 135] },
    });

    const startY = docPdf.lastAutoTable.finalY + 8;
    let x = 10;
    let y = startY;

    const addImage = (src, x0, y0, label) =>
      new Promise((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          docPdf.rect(x0 - 2, y0 - 2, IMG_WIDTH + 8, IMG_HEIGHT + 12);
          docPdf.addImage(img, "PNG", x0, y0, IMG_WIDTH, IMG_HEIGHT);
          docPdf.setFontSize(7);
          docPdf.text(label, x0, y0 + IMG_HEIGHT + 6);
          resolve();
        };
        img.onerror = () => resolve();
      });

    if (formLocal.cover) {
      tasks.push(addImage(getImageUrlLocal(formLocal.cover), x, y, "COVER"));
      x += 40;
    }

    if (formLocal.cup && formLocal.cover && formLocal.color) {
      const fullName = `${formLocal.cup}_${formLocal.cover}_${formLocal.color}`
        .replace(/\s/g, "")
        .toLowerCase();
      tasks.push(addImage(getImageUrlLocal(fullName), x, y, "CUP"));
      x += 40;
    }

    if (formLocal.carton) {
      tasks.push(addImage(getImageUrlLocal(formLocal.carton), x, y, "CARTON"));
      x += 40;
    }

    const sarfYStart = y + IMG_HEIGHT + 20;
    let sx = 10;
    let sy = sarfYStart;

    if (Array.isArray(formLocal.sarf)) {
      docPdf.setFontSize(9);
      docPdf.text("CONSUMABLES", sx, sy);
      sy += 4;

      formLocal.sarf.forEach((item) => {
        const cleaned = item.trim();
        tasks.push(addImage(getImageUrlLocal(cleaned), sx, sy, cleaned));
        sx += 40;
        if (sx > 170) {
          sx = 10;
          sy += IMG_HEIGHT + 15;
        }
      });
    }

    Promise.all(tasks).then(() => {
      const pageHeight = docPdf.internal.pageSize.getHeight();
      docPdf.setFontSize(9);
      docPdf.text("Bank Details", pageWidth - 70, pageHeight - 25);
      docPdf.setFontSize(8);
      docPdf.text("TNS EV GEREÇLERİ A.Ş.", pageWidth - 70, pageHeight - 20);
      docPdf.text("İŞ BANKASI / DES SANAYİ SİTESİ", pageWidth - 70, pageHeight - 15);
      docPdf.text("IBAN: TR42 0006 4000 0011 3530 0837 55", pageWidth - 70, pageHeight - 10);

      docPdf.save("offer-form.pdf");
    });
  };

  const createPDF = () => {
    if (!summary) {
      alert("To download the PDF, please click 'Prepare Offer' first.");
      return;
    }
    createAdvancedPDF(form, summary, usdRate, eurRate);
  };

  // === Offer preparation (USD conversion added for the unit price shown in Order Summary) ===
  const prepareOffer = () => {
    const missingFields = checkRequiredSelections();
    if (missingFields.length > 0) {
      alert(`Please complete the following selections first:\n\n${missingFields.join("\n")}`);
      return;
    }

    const a1 = parseAmount(form.amount1);
    const a2 = parseAmount(form.amount2);
    const a3 = parseAmount(form.amount3);
    if (a1 < 500 && a2 < 500 && a3 < 500) {
      alert("Please enter at least one quantity of 500 or more.");
      return;
    }

    const { tnsKar, farisKar } = calculateKarlar({
      secilenKapakMarka,
      secilenKapakFiyat,
      bardakFiyat,
      bardakBaskiFiyat,
      renkegoreFiyat,
      tnsKarMarji,
      farisKarMarji,
    });

    const birimMaliyet = hesaplaBirimToplamMaliyet({
      bardakFiyat,
      bardakBaskiFiyat,
      renkegoreFiyat,
      secilenKapakFiyat,
      ambalajFiyat,
      koliFiyat,
      serigrafiFiyat,
      sarfToplamFiyat,
      tnsKar,
      farisKar,
      secilenKapakMarka,
    });
    setBirimToplamMaliyet(birimMaliyet);

    const common = {
      birimMaliyet,
      koliKacli,
      koliBirimKoliFiyatiTl,
      koliFiyat,
      girilenIstanbulIci,
      secilenOdemeVadesiGun,
      params,
      getVadeFarkOrani,
    };

    const results = [];
    if (a1 >= 500) results.push({ key: "Qty_1", ...computePriceForAdet({ adet: a1, ...common }) });
    if (a2 >= 500) results.push({ key: "Qty_2", ...computePriceForAdet({ adet: a2, ...common }) });
    if (a3 >= 500) results.push({ key: "Qty_3", ...computePriceForAdet({ adet: a3, ...common }) });

    results.forEach((r) => {
      console.table({
        quantity: r.adet,
        unitCost: birimMaliyet,
        vadeTl: r.vadeTl,
        shippingTl: r.nakliyeTl,
        marginTl: r.karMarjiTl,
        cartonUnitTl_state: koliFiyat,
        unitNoVatTl: r.birimKDVHaric,
        unitWithVatTl: r.birimKDVDahil,
      });
    });

    const SiparişId = "ORD-" + Date.now();

    // NOTE: Kept Turkish carton description mapping (as-is) — internal preference
    const cartonPretty = (v) => {
      const map = {
        "6": "6'lı Separatörlü Master Karton",
        "12": "12'li Separatörlü Master Karton",
        "24": "24'lü Separatörlü Master Karton",
        "48": "48'li Master Karton",
      };
      const key = (v ?? "").toString().trim();
      return map[key] || key;
    };

    const baseSummary = {
      SiparişId,
      Tarih: new Date().toLocaleDateString("tr-TR"),
      USD: usdRate ? `${usdRate.toFixed(2)} TL` : "-",
      EUR: eurRate ? `${eurRate.toFixed(2)} TL` : "-",
      Bardak: form.cup?.replaceAll("_", " ") || "",
      Ambalaj: form.packaging || "Ambalaj yok",
      Kapak: form.cover || "",
      Renk: form.color || "",
      Serigrafi: form.print || "Baskı yok",
      "Koli Türü": cartonPretty(form.carton),
      "Sarf Malzemeleri": Array.isArray(form.sarf) && form.sarf.length > 0 ? form.sarf.join(", ") : "Yok",
      "Ödeme Vadesi": form.payment || "",
      Nakliye: form.transport || "",
    };

    const bardakGram = bardakAgirlik || 0;
    const kapakGram = covers.find((c) => c.id === form.cover)?.agirlik || 0;

    const adetBlocks = {};
    results.forEach((r) => {
      const Kapaktoplamkg = kapakGram * r.adet;
      const bardaktoplamkg = (bardakGram * r.adet) / 1000;

      const toplamKoliKg = (koliAgirlikKg || 0) * (r.gerekenKoli || 0);
      const toplamM3 = (koliHacimM3 || 0) * (r.gerekenKoli || 0);

      const toplamKg = Kapaktoplamkg + toplamKoliKg + bardaktoplamkg;

      // ✅ Convert shown unit price from TL -> USD (divide by usdRate)
      const usd = usdRate || 0;
      const unitUsd = usd > 0 ? r.birimKDVHaric / usd : 0;

      adetBlocks[r.key] = {
        adet: new Intl.NumberFormat("tr-TR").format(r.adet),

        // keep TL in case you need it later
        birim_kdv_haric_tl: r.birimKDVHaric.toLocaleString("tr-TR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),

        // ✅ USD display field (this is what we show in the Order Summary)
        birim_usd: unitUsd.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),

        gereken_koli: r.gerekenKoli,
        koli_olcusu: koliOlcusu || "-",
        toplam_agirlik_kg: toplamKg.toLocaleString("tr-TR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        toplam_hacim_m3: toplamM3.toLocaleString("tr-TR", {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        }),
      };
    });

    setSummary({ ...baseSummary, ...adetBlocks });
  };

  const checkRequiredSelections = () => {
    const missing = [];
    if (!form.material) missing.push("Material");
    if (!form.cup) missing.push("Cup");
    if (!form.cover) missing.push("Cover");
    if (!form.color) missing.push("Color");
    if (!form.packaging) missing.push("Packaging");
    if (!form.carton) missing.push("Carton Type");
    if (!form.print) missing.push("Print");
    if (!form.payment) missing.push("Payment Terms");
    if (!form.transport) missing.push("Delivery");

    const a1 = parseAmount(form.amount1);
    const a2 = parseAmount(form.amount2);
    const a3 = parseAmount(form.amount3);
    if (a1 === 0 && a2 === 0 && a3 === 0) missing.push("Quantity (enter at least one)");

    return missing;
  };

  // UI render helpers
  const renderCardGroup = (key, label) => {
  // Maxxon ve Middon bardakları hariç tut
  let filteredOptions = options[key];
  if (key === "cup") {
    filteredOptions = filteredOptions.filter(
      (opt) => opt !== "Maxxon_600ml" && opt !== "Middon_350ml"
    );
  }

  return (
    <div className="option-group" key={key}>
      <h4>{label}</h4>
      <div className={`option-images ${key}-group`}>
        {filteredOptions.map((opt) => {
          const cleanOpt = opt.replace(/\s+/g, "_").replace(/\.png$/i, "");
          const isSmallCup =
            key === "cup" && (opt === "Junior_500ml" || opt === "Middon_350ml");

          // ✅ SADECE GÖRÜNÜŞTE ÇEVİR: print kartlarında İngilizce göster
          const displayLabel = key === "print" ? tOption("print", opt) : opt;

          return (
            <div
              key={opt}
              className={`option-card ${form[key] === opt ? "selected" : ""} ${
                isSmallCup ? "centered-card" : ""
              }`}
              onClick={() => select(key, opt)} // ✅ value yine Türkçe kalıyor (Firebase eşleşmesi bozulmaz)
            >
              <div className="option-label">{displayLabel}</div>

              {/* print ve material için görsel yok; diğerleri için var */}
              {key !== "material" && key !== "print" && (
                <img
                  className={`option-img ${key}-img ${isSmallCup ? "small-cup" : ""}`}
                  src={getImageUrl(cleanOpt)}
                  alt={opt}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


  const renderImageOnlyCardGroup = (key, label) => {
    const imageMaps = {
      color: {
        Şeffaf: "Seffaf",
        Antrasit: "Antrasit",
        Kırmızı: "Kirmizi",
        "Koyu Mavi": "KoyuMavi",
        "Açık Mavi": "acikMavi",
        Mor: "Mor",
        Pembe: "Pembe",
        Sarı: "Sari",
        Turkuaz: "Turkuaz",
        Turuncu: "Turuncu",
        Yeşil: "Yesil",
      },
      packaging: {
        "Ambalaj yok": "ambalajyok",
        "Tekli Poşet": "tekliPoset",
        "Tekli Craft Kutu": "tekliCraftKutu",
      },
    };

    let values = options[key];
    if (key === "color") values = values.filter((v) => v !== "Özel_Renk");

    const chunked = [];
    for (let i = 0; i < values.length; i += 6) chunked.push(values.slice(i, i + 6));

    return (
      <div className="option-group" key={key}>
        <h4>{label}</h4>
        {chunked.map((row, rowIndex) => (
          <div className={`option-images ${key}-group`} key={rowIndex}>
            {row.map((opt) => {
              let displayLabel = opt;

              if (key === "carton") {
                displayLabel = displayMaps.carton?.[opt] || opt;
              } else {
                displayLabel = tOption(key, opt);
              }

              const imageId = imageMaps[key]?.[opt] || opt;

              return (
                <div key={opt} onClick={() => select(key, opt)}>
                  <div className="option-label outside-label">{displayLabel}</div>
                  <div className={`option-card ${form[key] === opt ? "selected" : ""}`}>
                    <img className={`option-img ${key}-img`} src={getImageUrl(imageId)} alt={displayLabel} />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const renderRadios = (key, label) => (
    <div className="option-group" key={key}>
      <h4>{label}</h4>
      {options[key].map((opt) => (
        <label key={opt}>
          <input type="radio" name={key} value={opt} checked={form[key] === opt} onChange={() => select(key, opt)} />{" "}
          {tOption(key, opt)}
        </label>
      ))}
    </div>
  );

  const sarfImages = [
    { file: "boyunluk", label: "Boyunluk" },
    { file: "sleeve", label: "Sleeve Kart" },
    { file: "sallanti", label: "Sallantı Kartı" },
    { file: "barkod", label: "Fiyat Barkodu" },
    { file: "etiket", label: "Koli Etiketi" },
    { file: "kilavuz", label: "Akordiyon kullanma kılavuzu" },
  ];

  return (
    <>
      {isBlocking && (
        <div className="block-ui-overlay">
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>Loading...</div>
        </div>
      )}

      <div className="App">
        <div className="config-panel">
          {renderCardGroup("material", "Material")}
          {renderCardGroup("cup", "Cup")}
          {renderCardGroup("cover", "Cover")}
          {renderImageOnlyCardGroup("color", "Color")}
          {renderImageOnlyCardGroup("packaging", "Packaging")}
          {renderImageOnlyCardGroup("carton", "Carton Type")}
          {renderCardGroup("print", "Screen Printing")}

          <div className="option-group">
            <h4>Consumables</h4>
            <div className="sarf-images">
              {sarfImages.map(({ file, label }) => {
                const isSelected = form.sarf?.includes(label);
                return (
                  <div
                    className={`sarf-item selectable ${isSelected ? "selected" : ""}`}
                    key={file}
                    onClick={() => toggleCheckbox("sarf", label)}
                  >
                    <div className="label">{tOption("sarf", label)}</div>
                    <img src={getImageUrl(file)} alt={label} />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="option-group">
            <label>Quantity 1:</label>
            <input
              name="amount1"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={formatNumber(form.amount1)}
              onChange={(e) => setForm((p) => ({ ...p, amount1: e.target.value }))}
            />
          </div>

          <div className="option-group">
            <label>Quantity 2:</label>
            <input
              name="amount2"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={formatNumber(form.amount2)}
              onChange={(e) => setForm((p) => ({ ...p, amount2: e.target.value }))}
            />
          </div>

          <div className="option-group">
            <label>Quantity 3:</label>
            <input
              name="amount3"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={formatNumber(form.amount3)}
              onChange={(e) => setForm((p) => ({ ...p, amount3: e.target.value }))}
            />
          </div>

          {renderRadios("payment", "Payment Terms")}
          {renderRadios("transport", "Delivery")}
        </div>

        <div className="summary-panel">
          <h3>Order Summary</h3>

          {form.cup && form.cover && form.color && (
            <div className="preview-box">
              <img
                src={getImageUrl(`${form.cup}_${form.cover}_${form.color}`)}
                alt={`${form.cup} - ${form.cover} - ${form.color}`}
                className="preview-img"
              />
            </div>
          )}

          {summary ? (
            <div className="formatted-summary">
              {/* keep this part as-is (your matching/combined naming) */}
              <h4 style={{ textAlign: "center", fontWeight: "bold", marginBottom: "16px" }}>
                {(form.cup + "_" + form.cover + "_" + form.color).toLowerCase()}
              </h4>

              <pre style={{ fontFamily: "monospace", fontSize: "14px", lineHeight: "1.8" }}>
                <strong>Date            :</strong> {summary.Tarih}
                {"\n"}
                <strong>USD Rate         :</strong> {summary.USD}
                {"\n"}
                <strong>EUR Rate         :</strong> {summary.EUR}
                {"\n"}
                <strong>Material        :</strong> {form.material || ""}
                {"\n"}
                {/* ✅ KEEP THESE TWO LABELS TURKISH (as requested) */}
                <strong>Bardak          :</strong> {summary.Bardak}
                {"\n"}
                <strong>Kapak           :</strong> {summary.Kapak}
                {"\n"}
                <strong>Color           :</strong> {tOption("color", summary.Renk)}
                {"\n"}
                <strong>Packaging       :</strong> {tOption("packaging", summary.Ambalaj)}
                {"\n"}
                <strong>Carton Type     :</strong> {summary["Koli Türü"] || form.carton || ""}
                {"\n"}
                <strong>Print           :</strong> {tOption("print", summary.Serigrafi)}
                {"\n"}
                <strong>Consumables     :</strong>{" "}
                {Array.isArray(form.sarf) && form.sarf.length > 0
                  ? form.sarf.map((x) => tOption("sarf", x)).join(", ")
                  : "None"}
                {"\n"}
                <strong>Payment Terms   :</strong> {tOption("payment", summary["Ödeme Vadesi"] || form.payment || "")}
                {"\n"}
                <strong>Delivery        :</strong> {tOption("transport", summary.Nakliye || form.transport || "")}
              </pre>

              {/* 3 quantity blocks — show UNIT PRICE IN USD (TL / USD rate) */}
              {["Qty_1", "Qty_2", "Qty_3"].map((k, idx) =>
                summary[k] ? (
                  <div className="adet-block" key={k}>
                    <div className="adet-title">{`Quantity ${idx + 1}`}</div>

                    <table className="kv-table">
                      <colgroup>
                        <col className="label" />
                        <col className="colon" />
                        <col />
                      </colgroup>
                      <tbody>
                        <tr>
                          <td>Quantity</td>
                          <td>:</td>
                          <td className="value">{summary[k].adet}</td>
                        </tr>
                        <tr>
                          <td>Unit Price</td>
                          <td>:</td>
                          <td className="value">{summary[k].birim_usd} USD</td>
                        </tr>
                        <tr>
                          <td>Required Cartons</td>
                          <td>:</td>
                          <td className="value">{summary[k].gereken_koli}</td>
                        </tr>
                        <tr>
                          <td>Carton Dimensions</td>
                          <td>:</td>
                          <td className="value">{summary[k].koli_olcusu}</td>
                        </tr>
                        <tr>
                          <td>Total Weight</td>
                          <td>:</td>
                          <td className="value">{summary[k].toplam_agirlik_kg} kg</td>
                        </tr>
                        <tr>
                          <td>Total Volume</td>
                          <td>:</td>
                          <td className="value">{summary[k].toplam_hacim_m3} m³</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : null
              )}

              <button className="button" onClick={createPDF}>
                Download PDF
              </button>
            </div>
          ) : (
            <p>Not prepared yet.</p>
          )}

          <button
            className="floating-offer-btn"
            onClick={prepareOffer}
            disabled={!usdRate || !eurRate}
            aria-label="Prepare Offer"
            title={!usdRate || !eurRate ? "Loading rates..." : "Prepare Offer"}
          >
            🧾 Prepare Offer
          </button>
        </div>
      </div>
    </>
  );
}

export default App;
